(() => {
  const dialog = document.querySelector('[data-product-image-viewer]');
  if (!dialog || dialog.dataset.initialized || !dialog.showModal) return;
  dialog.dataset.initialized = 'true';
  const mobile = matchMedia('(max-width:760px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion:reduce)');
  const track = dialog.querySelector('[data-viewer-track]');
  const previous = dialog.querySelector('[data-viewer-previous]');
  const next = dialog.querySelector('[data-viewer-next]');
  const close = dialog.querySelector('[data-viewer-close]');
  const counter = dialog.querySelector('[data-viewer-counter]');
  const hint = dialog.querySelector('[data-viewer-hint]');
  const bindings = new Map();
  let photos = [], index = 0, opener = null, restoreScroll = null, scrollFrame = 0;
  const configurations = [
    { root: '[data-relief-product]', stage: '[data-relief-gallery-stage]', image: '[data-relief-main-image]', button: '[data-relief-image]', source: 'reliefImage' },
    { root: '[data-simple-product]', stage: '.simple-photo', image: '[data-simple-main-image]', button: '[data-simple-gallery-src]', source: 'simpleGallerySrc' },
    { root: '[data-snowman-product]', stage: '[data-snowman-stage]', image: '[data-snowman-hero]', button: '[data-snowman-src]:not([data-snowman-preview-thumb])', source: 'snowmanSrc' }
  ];
  const imageKey = src => { try { const url = new URL(src, location.href); return url.origin + url.pathname; } catch { return src; } };
  const update = () => {
    counter.textContent = `${index + 1} / ${photos.length}`;
    previous.disabled = index === 0;
    next.disabled = index === photos.length - 1;
    hint.textContent = photos.length > 1 ? 'Swipe left or right' : 'Product image';
  };
  const goTo = (target, animate = true) => {
    const bounded = Math.max(0, Math.min(photos.length - 1, target));
    track.scrollTo({ left: bounded * track.clientWidth, behavior: reducedMotion.matches || !animate ? 'instant' : 'smooth' });
  };
  const open = binding => {
    if (!mobile.matches || dialog.open || binding.stage.classList.contains('is-live')) return;
    const current = binding.stage.querySelector(binding.config.image);
    if (!current?.getAttribute('src')) return;
    const currentKey = imageKey(current.src);
    const seen = new Set();
    photos = [...binding.root.querySelectorAll(binding.config.button)]
      .filter(button => !button.closest('[hidden]'))
      .map(button => {
        const source = button.dataset[binding.config.source];
        return { src: source?.startsWith('//') ? `https:${source}` : source, alt: button.querySelector('img')?.alt || current.alt };
      })
      .filter(photo => {
        if (!photo.src || seen.has(imageKey(photo.src))) return false;
        seen.add(imageKey(photo.src));
        return true;
      });
    index = photos.findIndex(photo => imageKey(photo.src) === currentKey);
    // A selected variant can have a featured image outside the thumbnail list.
    if (index < 0) { photos.unshift({ src: current.src, alt: current.alt }); index = 0; }
    const fragment = document.createDocumentFragment();
    photos.forEach((photo, position) => {
      const slide = document.createElement('div');
      slide.className = 'product-image-viewer__slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `Image ${position + 1} of ${photos.length}`);
      const image = document.createElement('img');
      image.alt = photo.alt || 'Product image';
      image.loading = Math.abs(position - index) <= 1 ? 'eager' : 'lazy';
      image.decoding = 'async';
      image.draggable = false;
      image.src = photo.src;
      slide.append(image);
      fragment.append(slide);
    });
    track.replaceChildren(fragment);
    opener = binding.stage;
    const body = document.body;
    const saved = ['position', 'top', 'left', 'right', 'width'].map(property => [property, body.style.getPropertyValue(property), body.style.getPropertyPriority(property)]);
    const x = window.scrollX, y = window.scrollY;
    restoreScroll = () => {
      saved.forEach(([property, value, priority]) => value ? body.style.setProperty(property, value, priority) : body.style.removeProperty(property));
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
    };
    Object.assign(body.style, { position: 'fixed', top: `${-y}px`, left: '0', right: '0', width: '100%' });
    dialog.showModal();
    track.scrollLeft = index * track.clientWidth;
    update();
    close.focus({ preventScroll: true });
  };
  dialog.addEventListener('close', () => {
    cancelAnimationFrame(scrollFrame);
    restoreScroll?.();
    restoreScroll = null;
    opener?.focus({ preventScroll: true });
    track.replaceChildren();
    photos = [];
  });
  close.addEventListener('click', () => dialog.close());
  previous.addEventListener('click', event => goTo(index - 1,event.detail>0));
  next.addEventListener('click', event => goTo(index + 1,event.detail>0));
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(() => {
      if (!dialog.open || !track.clientWidth || !photos.length) return;
      index = Math.max(0, Math.min(photos.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
      update();
    });
  }, { passive: true });
  dialog.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + (event.key === 'ArrowRight' ? 1 : -1),false);
    }
  });
  const refreshTrigger = binding => {
    const enabled = mobile.matches && !binding.stage.classList.contains('is-live');
    if (enabled) {
      binding.stage.dataset.productImageTrigger = '';
      binding.stage.setAttribute('role', 'button');
      binding.stage.setAttribute('tabindex', '0');
      binding.stage.setAttribute('aria-label', 'View product images full screen');
      binding.stage.setAttribute('aria-haspopup', 'dialog');
    } else {
      delete binding.stage.dataset.productImageTrigger;
      binding.attributes.forEach(([name, value]) => value === null ? binding.stage.removeAttribute(name) : binding.stage.setAttribute(name, value));
    }
  };
  const initialize = () => configurations.forEach(config => document.querySelectorAll(config.root).forEach(root => {
    const stage = root.querySelector(config.stage);
    if (!stage || !stage.querySelector(config.image) || bindings.has(stage)) return;
    const binding = { root, stage, config, attributes: ['role', 'tabindex', 'aria-label', 'aria-haspopup'].map(name => [name, stage.getAttribute(name)]) };
    bindings.set(stage, binding);
    const badge = document.createElement('span');
    badge.className = 'product-image-viewer__expand';
    badge.setAttribute('aria-hidden', 'true');
    badge.innerHTML = '<svg viewBox="0 0 24 24"><path d="M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5"/></svg>';
    stage.append(badge);
    let gesture = null, moved = false;
    stage.addEventListener('pointerdown', event => {
      moved = event.isPrimary === false || event.button !== 0;
      gesture = moved ? null : { x: event.clientX, y: event.clientY };
    });
    stage.addEventListener('pointermove', event => {
      if (gesture && (Math.abs(event.clientX - gesture.x) > 12 || Math.abs(event.clientY - gesture.y) > 12)) moved = true;
    });
    stage.addEventListener('pointercancel', () => { moved = true; gesture = null; });
    stage.addEventListener('click', event => { if (!moved || event.detail === 0) open(binding); });
    stage.addEventListener('keydown', event => {
      if (mobile.matches && ['Enter', ' '].includes(event.key)) { event.preventDefault(); open(binding); }
    });
    refreshTrigger(binding);
    const observer = new MutationObserver(() => refreshTrigger(binding));
    observer.observe(stage, { attributes: true, attributeFilter: ['class'] });
    binding.observer = observer;
  }));
  mobile.addEventListener('change', () => {
    if (!mobile.matches && dialog.open) dialog.close();
    bindings.forEach(refreshTrigger);
  });
  window.addEventListener('resize', () => { if (dialog.open) track.scrollLeft = index * track.clientWidth; });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => bindings.forEach((binding, stage) => {
    if (!event.target.contains(stage)) return;
    if (dialog.open && opener === stage) dialog.close();
    binding.observer.disconnect();
    bindings.delete(stage);
  }));
  initialize();
})();
